import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Store as ReduxStore } from "redux";

import {
  CartReduxAction,
  OrderModules,
  RootState,
  SentryLoggerInstance,
  StoreModules,
} from "../index";
import { OrderFinalisationStatusType } from "../redux_store/checkout/model";
import { HttpStatus } from "../services/httpClient";
import { isOrderStatusFailed } from "./useHandleOrderError";

export const UPDATE_ORDER_HOOK_ORDER_404 =
  "[APP]useCreateUpdateOrder: Order Response 404 - recreating order. Route:";

const useCreateUpdateOrder = (
  store: ReduxStore,
  routeName: string,
  shouldMonitorBasketChangesAndUpdateOrder?: boolean
) => {
  const dispatch = useDispatch();
  const {
    orderASAP,
    orderCollectionType,
    orderTime,
    tableNumber,
    getOrderResponse,
    getOrderError,
    orderStatus,
  } = useSelector((s: RootState) => s.order);
  const { totalPrice: cartTotalPrice } = useSelector(
    (state: RootState) => state.cart
  );
  const { selectedStore, storeOrderTimes, storeOrderOffset } = useSelector(
    (s: RootState) => s.store
  );
  const { posMenuId } = useSelector((state: RootState) => state.menu);
  const { items: cartItems, shouldUpdateOrder } = useSelector(
    (s: RootState) => s.cart
  );
  const { activeReward, redeemDollars } = useSelector(
    (state: RootState) => state.loyalty
  );
  const {
    loading: checkoutLoading,
    paymentResponse,
    error: checkoutError,
  } = useSelector((s: RootState) => s.checkout);

  const isOrderFailed = useCallback(
    (orderId?: string) => {
      return (
        (getOrderResponse?.orderFinalisationStatus ===
          OrderFinalisationStatusType.FAILED &&
          getOrderResponse?.orderId === orderId) ||
        isOrderStatusFailed(orderStatus)
      );
    },
    [
      getOrderResponse?.orderFinalisationStatus,
      getOrderResponse?.orderId,
      orderStatus,
    ]
  );

  const isOrderAlreadyInProgress = useCallback(
    (orderId?: string) => {
      let isInProgress =
        getOrderResponse?.orderFinalisationStatus !=
          OrderFinalisationStatusType.CREATED &&
        getOrderResponse?.orderId === orderId;
      if (orderStatus) {
        isInProgress =
          orderStatus?.orderFinalisationStatus !=
            OrderFinalisationStatusType.CREATED &&
          orderStatus?.orderId === orderId;
      }
      //Can not update order if checkout response received
      if (paymentResponse?.order?.orderId === orderId) {
        isInProgress = true;
      }
      return isInProgress;
    },
    [
      getOrderResponse?.orderFinalisationStatus,
      getOrderResponse?.orderId,
      orderStatus,
      paymentResponse?.order?.orderId,
    ]
  );

  const createOrUpdateOrderCb = useCallback(
    (orderId?: string, triggeredByOrderTime?: boolean) => {
      if (
        selectedStore &&
        !checkoutLoading &&
        (!isOrderAlreadyInProgress(orderId) ||
          isOrderFailed(orderId) ||
          !orderId)
      ) {
        //get next available time, triggeredByOrderTime called when time expired
        let finalTime = orderTime || 0;
        if (triggeredByOrderTime) {
          const calculatedAsapTime =
            StoreModules.StoreUtils.calculateAsapTimeFromOffset(
              cartTotalPrice,
              storeOrderOffset
            ).valueOf();

          const time = new Date(
            OrderModules.OrderUtils.getNextAvailableTime(
              store,
              calculatedAsapTime,
              storeOrderTimes
            )
          );
          //check if order is ASAP or scheduled is earlier then next possible time
          if (orderASAP || time.valueOf() > orderTime) {
            finalTime = time.valueOf();
          }
        }

        OrderModules.OrderUtils.createOrUpdateOrder(
          store,
          posMenuId,
          orderCollectionType,
          cartItems,
          finalTime,
          orderASAP,
          selectedStore,
          tableNumber,
          selectedStore.timeZoneInfo.storeTimeZone,
          isOrderFailed(orderId) ? null : orderId, //Recreate order if status is failed or there is no orderId
          !!triggeredByOrderTime,
          activeReward,
          redeemDollars
        );
      }
    },
    [
      selectedStore,
      checkoutLoading,
      isOrderAlreadyInProgress,
      isOrderFailed,
      orderTime,
      store,
      posMenuId,
      orderCollectionType,
      cartItems,
      orderASAP,
      tableNumber,
      activeReward,
      redeemDollars,
      cartTotalPrice,
      storeOrderOffset,
      storeOrderTimes,
    ]
  );

  useEffect(() => {
    //create or update order when items are added to the cart
    if (
      shouldMonitorBasketChangesAndUpdateOrder === true &&
      shouldUpdateOrder &&
      (cartItems.length > 0 || !getOrderResponse?.orderId)
    ) {
      createOrUpdateOrderCb(getOrderResponse?.orderId);
      dispatch(CartReduxAction.setShouldUpdateOrder(false));
    }
  }, [
    cartItems,
    createOrUpdateOrderCb,
    dispatch,
    getOrderResponse?.orderId,
    shouldMonitorBasketChangesAndUpdateOrder,
    shouldUpdateOrder,
  ]);

  //create new order when 404 error came
  useEffect(() => {
    if (
      getOrderError?.statusCode == HttpStatus.NotFound &&
      getOrderResponse &&
      shouldMonitorBasketChangesAndUpdateOrder === true
    ) {
      // 404 order patch can occur when checkout previously was
      // "BraintreeTransactionCreationFailedException" allow order recreate
      // or when the coupon is already claimed
      createOrUpdateOrderCb();
      SentryLoggerInstance.sentryCaptureCustomError(
        `${UPDATE_ORDER_HOOK_ORDER_404} ${routeName}`,
        {}
      );
    }
  }, [
    checkoutError,
    createOrUpdateOrderCb,
    dispatch,
    getOrderError,
    getOrderResponse,
    routeName,
    shouldMonitorBasketChangesAndUpdateOrder,
  ]);

  return createOrUpdateOrderCb;
};

export default useCreateUpdateOrder;
